import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
} from "reactstrap";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { PaginationControl } from "react-bootstrap-pagination-control";
import debounce from "lodash.debounce";
import { t } from "i18next";

import { ITEMS_PER_PAGE } from "../../constants";
import { numberFormatter } from "../../helpers";
import EmployeeService from "../../services/employee";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";

import NoRecords from "../../components/Table/NoRecords";
import SortableHeader from "../../components/SortableHeader";
// import ActionBtns from "../../components/ActionBtns";
import WithdrawalCharges from "./WithdrawalCharges.modal";

const HeaderList = [
  {
    title: t("DATE"),
    value: "date",
    style: { minWidth: "150px", width: "30%" },
  },
  {
    title: t("CHEQUE_NUMBER"),
    value: "reference",
    style: { minWidth: "150px", width: "30%" },
  },
  {
    title: t("EMPLOYEE.AMT_OF_WITHDRAWAL"),
    value: "amount",
    isNumeric: true,
    style: { minWidth: "200px", width: "30%", textAlign: "right" },
  },
  {
    title: t("EMPLOYEE.EMP_BALANCE"),
    value: "employeeBalance",
    isNumeric: true,
    style: { minWidth: "150px", width: "30%", textAlign: "right" },
  },
];

const reqPayload = { pageNo: 1 };

function Withdrawal() {
  const dispatch = useDispatch();
  const params = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [employeeData, setEmployeeData] = useState([]);
  const [withdrawalModal, setWithdrawalModal] = useState({
    shown: false,
    data: {},
  });
  const [chargesSearch, setChargesSearch] = useState();

  useEffect(() => {
    reqPayload["employeeId"] = params.employeeId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    delete reqPayload["search"];
    if (search) {
      reqPayload["search"] = search;
    }
    getWithdrawal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function getWithdrawal() {
    dispatch(setLoadingOverlay(true));
    await EmployeeService.listWithdrawal(reqPayload)
      .then((response) => {
        const { status, message, dataList, totalCount } = response.data;

        if (status === 400) {
          toast.error(message);
          return;
        }

        if (dataList && dataList.length) {
          dataList.forEach((item) => {
            item.formattedAmount = numberFormatter(item.amount);
            item.formattedEmpBal = numberFormatter(item.employeeBalance);
          });

          setEmployeeData(dataList);
          setTotalCount(totalCount);
        }
      })
      .finally(() => {
        setIsLoading(false);
        dispatch(setLoadingOverlay(false));
      });
  }

  const viewWithdrawalDetailsFn = (l0Idx) => {
    setWithdrawalModal({
      shown: true,
      data: employeeData[l0Idx],
    });
  };

  const handlePageClick = (currentPage) => {
    reqPayload["pageNo"] = currentPage;
    setCurrentPage(currentPage);
    getWithdrawal();
  };

  const sortBy = (sortObj) => {
    delete reqPayload["sort"];
    if (sortObj) {
      reqPayload["sort"] = [sortObj];
    }
    getWithdrawal();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeHandler = useCallback(debounce(updateSearch, 500), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChargesChangeHandler = useCallback(
    debounce(updateChargesSearch, 500),
    []
  );

  function updateSearch(e) {
    setSearch(e.target.value?.trim());
  }

  function updateChargesSearch(e) {
    setChargesSearch(e.target.value?.trim());
  }

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
          <InputGroup>
            <Input
              onChange={debouncedChangeHandler}
              placeholder={t("SEARCH")}
            />
            <Button color="primary" type="button">
              <i className="ri-search-line"></i>
            </Button>
          </InputGroup>
        </Form>
      </div>

      <Table striped bordered responsive size="sm">
        <thead>
          <tr>
            <SortableHeader
              headers={HeaderList}
              isDisabled={employeeData.length === 0}
              sortClick={sortBy}
            />
          </tr>
        </thead>

        <tbody>
          {employeeData.map((item, l0Idx) => (
            <tr key={l0Idx}>
              <td className="text-center">
                <Button
                  color="primary"
                  size="sm"
                  title={t("EMPLOYEE.VIEW_CHARGES_DETAIL")}
                  onClick={() => viewWithdrawalDetailsFn(l0Idx)}
                >
                  {t("VIEW_TITLE")}
                </Button>
              </td>
              <td>{item.date}</td>
              <td>{item.reference}</td>
              <td align="right">{item.formattedAmount}</td>
              <td align="right">{item.formattedEmpBal}</td>
            </tr>
          ))}

          {!isLoading && employeeData.length === 0 && (
            <NoRecords colSpan={HeaderList.length + 1} />
          )}
        </tbody>
      </Table>

      <div className="custom-pagination mt-2">
        <PaginationControl
          page={currentPage}
          total={totalCount}
          limit={ITEMS_PER_PAGE}
          changePage={handlePageClick}
          next={true}
          last={true}
          ellipsis={1}
        />
      </div>

      <Modal
        id="emp-charges-modal"
        isOpen={withdrawalModal.shown}
        onClosed={() => setChargesSearch("")}
        backdrop="static"
        className="modal-dialog-centered"
      >
        <ModalHeader
          toggle={() => {
            setWithdrawalModal({
              shown: false,
              data: {},
            });
          }}
        >
          <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
            <InputGroup>
              <Input
                onChange={debouncedChargesChangeHandler}
                placeholder={t("SEARCH")}
              />
              <Button color="primary" type="button">
                <i className="ri-search-line"></i>
              </Button>
            </InputGroup>
          </Form>
        </ModalHeader>

        <ModalBody>
          <WithdrawalCharges
            data={withdrawalModal.data}
            search={chargesSearch}
          />
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

export default Withdrawal;